/**
 * DoseSourceIndicator — UI-03 tri-state (UI-SPEC líneas 167-178).
 *
 * Backend literales reales:
 *   "WHO-MF-2008" | "DailyMed"  → verde "dosis verificada"
 *   "RECHAZADO"                 → rojo "fuera de rango, consultar"
 *   null o desconocido          → naranja "verificar" (defensive default)
 *
 * Pitfall #1 RESOLVED (RESEARCH §Open Questions Q1): el defensive default
 * cubre el gap LME-24 backend hasta que Phase 1 cierre la curaduría completa.
 *
 * Drimian P9: la incertidumbre es VISIBLE, no enterrada.
 */
import { Icon } from './icons'
import type { DoseSource } from '../types/product_card'

interface Props {
  source: DoseSource | null
  /** 2026-09-30 — con EVIDENCE_REVIEWED/CLINICALLY_REVIEWED y fuente, se muestra la fuente. */
  reviewStatus?: string | null
  fuente?: string | null
  url?: string | null
  nota?: string | null
}

export function DoseSourceIndicator({ source, reviewStatus, fuente, url, nota }: Props) {
  const conEvidencia =
    source !== 'RECHAZADO' &&
    !!fuente &&
    (reviewStatus === 'EVIDENCE_REVIEWED' || reviewStatus === 'CLINICALLY_REVIEWED')
  if (conEvidencia) {
    return (
      <div className="grid gap-1">
        <div
          role="status"
          className="inline-flex items-center gap-1.5 text-[13px] font-mono tracking-wide"
          style={{
            padding: '4px 8px',
            borderRadius: 'var(--radius-s)',
            background: 'color-mix(in oklch, var(--color-ok) 12%, transparent)',
            color: 'var(--color-ok)',
            fontFamily: 'var(--font-mono)',
            justifySelf: 'start',
          }}
        >
          ● Dosis según{' '}
          {url ? (
            <a href={url} target="_blank" rel="noreferrer" className="underline">
              {fuente}
            </a>
          ) : (
            fuente
          )}
        </div>
        {nota && (
          // 2026-10-07: la nota transcribe la ficha (texto regulatorio): plegada, a mano si se necesita
          <details className="text-[12px] leading-snug" style={{ color: 'var(--color-text-muted)' }}>
            <summary style={{ cursor: 'pointer' }}>ver nota</summary>
            {nota}
          </details>
        )}
      </div>
    )
  }

  if (source === 'RECHAZADO') {
    return (
      <div
        role="status"
        className="inline-flex items-center gap-1.5 text-[12px] font-mono uppercase tracking-wider"
        style={{
          padding: '4px 8px',
          borderRadius: 'var(--radius-s)',
          background: 'var(--color-danger-soft)',
          color: 'var(--color-danger)',
          fontFamily: 'var(--font-mono)',
        }}
      >
        <Icon.Stop width={14} height={14} aria-hidden="true" />
        dosis fuera de rango, consultar
      </div>
    )
  }

  if (source) {
    return (
      <div
        role="status"
        className="inline-flex items-center gap-1.5 text-[12px] font-mono uppercase tracking-wider"
        style={{
          padding: '4px 8px',
          borderRadius: 'var(--radius-s)',
          background: 'color-mix(in oklch, var(--color-ok) 12%, transparent)',
          color: 'var(--color-ok)',
          fontFamily: 'var(--font-mono)',
        }}
      >
        <Icon.Check width={14} height={14} aria-hidden="true" />
        dosis verificada
      </div>
    )
  }

  // null o string desconocido → defensive default (Pitfall #1 resolution)
  return (
    <div
      role="status"
      className="inline-flex items-center gap-1.5 text-[12px] font-mono uppercase tracking-wider"
      style={{
        padding: '4px 8px',
        borderRadius: 'var(--radius-s)',
        background: 'var(--color-warn-soft)',
        color: 'var(--color-warn)',
        fontFamily: 'var(--font-mono)',
      }}
    >
      <Icon.Alert width={14} height={14} aria-hidden="true" />
      dosis sugerida por IA, verificar
    </div>
  )
}
